/**
 * Migration Script: Convert Hardcoded Layouts to System Admin
 *
 * This script reads existing hardcoded page layouts from each application
 * and creates them as dynamic layouts in System Admin.
 *
 * Run once to populate System Admin with current layouts.
 */

import {
  createPageLayoutTemplate,
  createLayoutSection,
  addFieldToSection,
  createLayoutAssignment
} from '../services/api/uiConfigurationApi';

// ============================================
// HARDCODED LAYOUT DEFINITIONS
// ============================================

// CyberSweep Client Detail Layout
const CYBERSWEEP_CLIENT_LAYOUT = {
  name: 'cybersweep_client_detail',
  label: 'CyberSweep Client Detail',
  application: 'cybersweep',
  entityType: 'client',
  description: 'Default layout for CyberSweep client records',
  sections: [
    {
      name: 'basic_information',
      label: 'Basic Information',
      order: 1,
      columns: 2,
      collapsible: false,
      fields: [
        { fieldName: 'clientName__c', fieldLabel: 'Client Name', order: 1, column: 1, required: true },
        { fieldName: 'clientNumber__c', fieldLabel: 'Client Number', order: 2, column: 1, required: false },
        { fieldName: 'emailAddress__c', fieldLabel: 'Email', order: 3, column: 1, required: true },
        { fieldName: 'phoneNumber__c', fieldLabel: 'Phone', order: 4, column: 2, required: false },
        { fieldName: 'website__c', fieldLabel: 'Website', order: 5, column: 2, required: false }
      ]
    },
    {
      name: 'address_information',
      label: 'Address Information',
      order: 2,
      columns: 2,
      collapsible: true,
      fields: [
        { fieldName: 'mailingAddress__c', fieldLabel: 'Mailing Address', order: 1, column: 1, required: false },
        { fieldName: 'billingAddress__c', fieldLabel: 'Billing Address', order: 2, column: 2, required: false }
      ]
    },
    {
      name: 'cybersweep_info',
      label: 'CyberSweep Information',
      order: 3,
      columns: 2,
      collapsible: true,
      fields: [
        { fieldName: 'lastAuditDate__c', fieldLabel: 'Last Audit Date', order: 1, column: 1, required: false },
        { fieldName: 'nextAuditDate__c', fieldLabel: 'Next Audit Date', order: 2, column: 1, required: false },
        { fieldName: 'complianceScore__c', fieldLabel: 'Compliance Score', order: 3, column: 2, required: false },
        { fieldName: 'riskLevel__c', fieldLabel: 'Risk Level', order: 4, column: 2, required: false }
      ]
    }
  ]
};

// Credit Management Case Layout
const CREDIT_MANAGEMENT_CASE_LAYOUT = {
  name: 'credit_case_detail',
  label: 'Credit Case Detail',
  application: 'credit-management',
  entityType: 'case',
  description: 'Default layout for Credit Management cases',
  sections: [
    {
      name: 'case_information',
      label: 'Case Information',
      order: 1,
      columns: 2,
      collapsible: false,
      fields: [
        { fieldName: 'caseNumber__c', fieldLabel: 'Case Number', order: 1, column: 1, required: true },
        { fieldName: 'caseStatus__c', fieldLabel: 'Status', order: 2, column: 1, required: true },
        { fieldName: 'priority__c', fieldLabel: 'Priority', order: 3, column: 2, required: true },
        { fieldName: 'caseType__c', fieldLabel: 'Case Type', order: 4, column: 2, required: true }
      ]
    },
    {
      name: 'credit_details',
      label: 'Credit Details',
      order: 2,
      columns: 2,
      collapsible: true,
      fields: [
        { fieldName: 'creditScore__c', fieldLabel: 'Credit Score', order: 1, column: 1, required: false },
        { fieldName: 'creditLimit__c', fieldLabel: 'Credit Limit', order: 2, column: 1, required: false },
        { fieldName: 'outstandingBalance__c', fieldLabel: 'Outstanding Balance', order: 3, column: 2, required: false },
        { fieldName: 'paymentTerms__c', fieldLabel: 'Payment Terms', order: 4, column: 2, required: false }
      ]
    },
    {
      name: 'dates_and_tracking',
      label: 'Dates & Tracking',
      order: 3,
      columns: 2,
      collapsible: true,
      fields: [
        { fieldName: 'createdDate__c', fieldLabel: 'Created Date', order: 1, column: 1, required: false },
        { fieldName: 'lastModifiedDate__c', fieldLabel: 'Last Modified', order: 2, column: 1, required: false },
        { fieldName: 'closedDate__c', fieldLabel: 'Closed Date', order: 3, column: 2, required: false },
        { fieldName: 'assignedTo__c', fieldLabel: 'Assigned To', order: 4, column: 2, required: false }
      ]
    }
  ]
};

// Credit Block Client Layout
const CREDIT_BLOCK_CLIENT_LAYOUT = {
  name: 'credit_block_client_detail',
  label: 'Credit Block Client Detail',
  application: 'credit-block',
  entityType: 'client',
  description: 'Default layout for Credit Block client records',
  sections: [
    {
      name: 'client_information',
      label: 'Client Information',
      order: 1,
      columns: 2,
      collapsible: false,
      fields: [
        { fieldName: 'clientName__c', fieldLabel: 'Client Name', order: 1, column: 1, required: true },
        { fieldName: 'clientId__c', fieldLabel: 'Client ID', order: 2, column: 1, required: true },
        { fieldName: 'email__c', fieldLabel: 'Email', order: 3, column: 2, required: true },
        { fieldName: 'phone__c', fieldLabel: 'Phone', order: 4, column: 2, required: false }
      ]
    },
    {
      name: 'credit_bureau_info',
      label: 'Credit Bureau Information',
      order: 2,
      columns: 2,
      collapsible: true,
      fields: [
        { fieldName: 'equifaxStatus__c', fieldLabel: 'Equifax Status', order: 1, column: 1, required: false },
        { fieldName: 'experianStatus__c', fieldLabel: 'Experian Status', order: 2, column: 1, required: false },
        { fieldName: 'transunionStatus__c', fieldLabel: 'TransUnion Status', order: 3, column: 2, required: false },
        { fieldName: 'blockStatus__c', fieldLabel: 'Overall Block Status', order: 4, column: 2, required: false }
      ]
    }
  ]
};

// Regal Main Contact Layout
const REGAL_MAIN_CONTACT_LAYOUT = {
  name: 'regal_contact_detail',
  label: 'Regal Contact Detail',
  application: 'regal-main',
  entityType: 'contact',
  description: 'Default layout for Regal main contact records',
  sections: [
    {
      name: 'contact_information',
      label: 'Contact Information',
      order: 1,
      columns: 2,
      collapsible: false,
      fields: [
        { fieldName: 'firstName__c', fieldLabel: 'First Name', order: 1, column: 1, required: true },
        { fieldName: 'lastName__c', fieldLabel: 'Last Name', order: 2, column: 1, required: true },
        { fieldName: 'email__c', fieldLabel: 'Email', order: 3, column: 2, required: true },
        { fieldName: 'phone__c', fieldLabel: 'Phone', order: 4, column: 2, required: false },
        { fieldName: 'title__c', fieldLabel: 'Title', order: 5, column: 1, required: false },
        { fieldName: 'company__c', fieldLabel: 'Company', order: 6, column: 2, required: false }
      ]
    },
    {
      name: 'address',
      label: 'Address',
      order: 2,
      columns: 2,
      collapsible: true,
      fields: [
        { fieldName: 'street__c', fieldLabel: 'Street', order: 1, column: 1, required: false },
        { fieldName: 'city__c', fieldLabel: 'City', order: 2, column: 1, required: false },
        { fieldName: 'state__c', fieldLabel: 'State', order: 3, column: 2, required: false },
        { fieldName: 'zipCode__c', fieldLabel: 'Zip Code', order: 4, column: 2, required: false }
      ]
    }
  ]
};

// ============================================
// MIGRATION FUNCTIONS
// ============================================

async function migrateLayout(layoutDef: any): Promise<void> {
  console.log(`\n🔄 Migrating layout: ${layoutDef.label}`);

  try {
    // Step 1: Create the layout
    console.log(`  ➤ Creating layout...`);
    const layoutResponse = await createPageLayoutTemplate({
      name: layoutDef.name,
      application: layoutDef.application,
      description: layoutDef.description,
      isPublic: false,
      isDefault: true,
      sections: []
    });

    const layout = layoutResponse.data;
    console.log(`  ✅ Layout created: ${layout.id}`);

    // Step 2: Create sections
    for (const sectionDef of layoutDef.sections) {
      console.log(`  ➤ Creating section: ${sectionDef.label}`);

      const sectionResponse = await createLayoutSection(layout.id, {
        name: sectionDef.name,
        label: sectionDef.label,
        order: sectionDef.order,
        columns: sectionDef.columns,
        collapsible: sectionDef.collapsible,
        collapsed: false
      });

      const section = sectionResponse.data;
      console.log(`    ✅ Section created: ${section.id}`);

      // Step 3: Add fields to section
      for (const fieldDef of sectionDef.fields) {
        console.log(`    ➤ Adding field: ${fieldDef.fieldLabel}`);

        await addFieldToSection(layout.id, section.id, {
          fieldId: `field_${fieldDef.fieldName}`,
          fieldName: fieldDef.fieldName,
          fieldLabel: fieldDef.fieldLabel,
          fieldType: 'text', // Default, should be read from custom field definition
          order: fieldDef.order,
          column: fieldDef.column,
          required: fieldDef.required,
          readonly: false,
          width: 'full'
        });

        console.log(`      ✅ Field added`);
      }
    }

    // Step 4: Create default assignment
    console.log(`  ➤ Creating default assignment...`);
    await createLayoutAssignment(layout.id, {
      recordType: 'standard',
      isDefault: true,
      priority: 1
    });
    console.log(`  ✅ Default assignment created`);

    console.log(`✅ Layout migration complete: ${layoutDef.label}\n`);

  } catch (error) {
    console.error(`❌ Failed to migrate layout: ${layoutDef.label}`, error);
    throw error;
  }
}

// ============================================
// MAIN MIGRATION FUNCTION
// ============================================

export async function migrateAllLayouts(): Promise<void> {
  console.log('========================================');
  console.log('🚀 Starting Layout Migration to System Admin');
  console.log('========================================\n');

  const layouts = [
    CYBERSWEEP_CLIENT_LAYOUT,
    CREDIT_MANAGEMENT_CASE_LAYOUT,
    CREDIT_BLOCK_CLIENT_LAYOUT,
    REGAL_MAIN_CONTACT_LAYOUT
  ];

  let successCount = 0;
  let failCount = 0;

  for (const layoutDef of layouts) {
    try {
      await migrateLayout(layoutDef);
      successCount++;
    } catch (error) {
      failCount++;
      console.error(`Failed to migrate ${layoutDef.label}:`, error);
    }
  }

  console.log('\n========================================');
  console.log('📊 Migration Summary');
  console.log('========================================');
  console.log(`✅ Successfully migrated: ${successCount} layout(s)`);
  console.log(`❌ Failed: ${failCount} layout(s)`);
  console.log('========================================\n');

  if (failCount === 0) {
    console.log('🎉 All layouts migrated successfully!');
    console.log('💡 Next steps:');
    console.log('   1. Verify layouts in System Admin → Page Layouts');
    console.log('   2. Update application code to use dynamic layout loader');
    console.log('   3. Test layouts in each application');
  }
}

// Run migration if executed directly
if (require.main === module) {
  migrateAllLayouts()
    .then(() => process.exit(0))
    .catch((error) => {
      console.error('Migration failed:', error);
      process.exit(1);
    });
}
